import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LeadSchema, type CampaignDto, type EmployeeDto, type LeadDto } from '@taqon/contracts';
import { useNavigate, useSearchParams } from 'react-router';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { SearchFilterBar } from '../../shared/ui/filters';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ActiveEmployeePicker, EmployeeName, ListSelect, Pager } from '../crm/common';
import { RecordLink } from '../finance/detail';
import { UrlDimensionNote } from '../reporting/dimensions';
import { CampaignPicker, CampaignRef, LEADS, relationOf, useLeadLists, valueLabel } from './common';

const PAGE_SIZE = 50;

/**
 * The lead register (FRD-MKT-005, MKT-044): operational lead reporting by source, campaign,
 * status, priority, industry, assigned employee, qualification status, conversion and the date
 * the lead was recorded, with a search over the Lead ID, company and contact — filtered and paged
 * on the server and kept in the URL so a refresh keeps them. Where the session may, a new lead is
 * recorded here; the Lead ID is generated by the system.
 */
export function LeadsPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'marketing.lead:create');
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const canSeeCampaigns = hasCapability(session.data, 'marketing.campaign:view');
  const lists = useLeadLists();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [assignee, setAssignee] = useState<EmployeeDto | null>(null);
  const [campaign, setCampaign] = useState<CampaignDto | null>(null);
  const [search, setSearch] = useState(params.get('search') ?? '');

  const filters = {
    source: params.get('source') ?? '',
    campaign: params.get('campaign') ?? '',
    status: params.get('status') ?? '',
    priority: params.get('priority') ?? '',
    industry: params.get('industry') ?? '',
    qualification: params.get('qualification') ?? '',
    assignee: params.get('assignee') ?? '',
    converted: params.get('converted') ?? '',
    open: params.get('open') ?? '',
    convertedFrom: params.get('converted_from') ?? '',
    convertedTo: params.get('converted_to') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    search: params.get('search') ?? '',
  };
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  // The URL names of the filters whose key differs (the dashboards link here, FRD-RPT-007).
  const urlKey = (key: keyof typeof filters | 'page'): string =>
    key === 'convertedFrom' ? 'converted_from' : key === 'convertedTo' ? 'converted_to' : key;
  const set = (key: keyof typeof filters | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(urlKey(key));
    else next.set(urlKey(key), value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: 'code',
  });
  const apiFilters: readonly [string, string][] = [
    ['source', filters.source],
    ['campaign_id', filters.campaign],
    ['status', filters.status],
    ['priority', filters.priority],
    ['industry', filters.industry],
    ['qualification_status', filters.qualification],
    ['assigned_employee_id', filters.assignee],
    ['converted', filters.converted],
    ['open', filters.open],
    ['converted_from', filters.convertedFrom],
    ['converted_to', filters.convertedTo],
    ['created_from', filters.from],
    ['created_to', filters.to],
    ['search', filters.search],
    // A dashboard's client dimension (FRD-RPT-010).
    ['client_id', params.get('client_id') ?? ''],
  ];
  for (const [key, value] of apiFilters) if (value !== '') query.set(`filter[${key}]`, value);
  const leads = useQuery({
    queryKey: ['marketing', 'leads', 'list', query.toString()],
    queryFn: () => getPage(`${LEADS}?${query.toString()}`, LeadSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Leads"
        description="The lead register: prospects before they become clients."
        actions={
          <>
            <ExportButton recordType="marketing.lead" href={exportHref(LEADS, query)} />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCreating(true);
                }}
              >
                New lead
              </Button>
            )}
          </>
        }
      />
      <UrlDimensionNote keys={['client_id']} />
      <SearchFilterBar
        aria-label="Lead filters"
        label="Search by ID, company or contact"
        value={search}
        onChange={setSearch}
        onBlur={() => {
          if (search.trim() !== filters.search) set('search', search.trim());
        }}
        onSubmit={() => {
          set('search', search.trim());
        }}
      >
        {(
          [
            ['status', 'Status', lists.data?.status],
            ['source', 'Source', lists.data?.source],
            ['priority', 'Priority', lists.data?.priority],
            ['industry', 'Industry', lists.data?.industry],
            ['qualification', 'Qualification', lists.data?.qualificationStatus],
          ] as const
        ).map(([key, label, values]) => (
          <TextField
            key={key}
            select
            size="small"
            label={label}
            value={filters[key]}
            onChange={(e) => {
              set(key, e.target.value);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {(values ?? []).map((value) => (
              <MenuItem key={value.valueCode} value={value.valueCode}>
                {value.label}
                {value.active ? '' : ' (inactive)'}
              </MenuItem>
            ))}
          </TextField>
        ))}
        <TextField
          select
          size="small"
          label="Converted"
          value={filters.converted}
          onChange={(e) => {
            set('converted', e.target.value);
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Converted</MenuItem>
          <MenuItem value="false">Not converted</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Open"
          value={filters.open}
          onChange={(e) => {
            set('open', e.target.value);
          }}
          helperText="Open: not yet related to a client"
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Open leads</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="date"
          label="Converted from"
          value={filters.convertedFrom}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('convertedFrom', e.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Converted to"
          value={filters.convertedTo}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('convertedTo', e.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Recorded from"
          value={filters.from}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('from', e.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Recorded to"
          value={filters.to}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('to', e.target.value);
          }}
        />
        {canSeeEmployees &&
          (filters.assignee !== '' && assignee === null ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2">
                Assigned to: <EmployeeName id={filters.assignee} />
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  set('assignee', '');
                }}
              >
                Clear
              </Button>
            </Stack>
          ) : (
            <ActiveEmployeePicker
              label="Assigned to"
              value={assignee}
              onChange={(value) => {
                setAssignee(value);
                set('assignee', value?.id ?? '');
              }}
            />
          ))}
        {canSeeCampaigns &&
          (filters.campaign !== '' && campaign === null ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2">Campaign filter applied</Typography>
              <Button
                size="small"
                onClick={() => {
                  set('campaign', '');
                }}
              >
                Clear
              </Button>
            </Stack>
          ) : (
            <CampaignPicker
              label="Campaign"
              value={campaign}
              onChange={(value) => {
                setCampaign(value);
                set('campaign', value?.id ?? '');
              }}
            />
          ))}
      </SearchFilterBar>
      {leads.isError && <ErrorState message={leads.error.message} />}
      <Section>
        {leads.isPending && <LoadingState label="Loading leads" />}
        {leads.data?.items.length === 0 && <EmptyState title="No leads found" />}
        {leads.data !== undefined && leads.data.items.length > 0 && (
          <>
            <LeadTable leads={leads.data.items} />
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={leads.data.total}
              noun="lead(s)"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
      {creating && (
        <LeadDialog
          onClose={(saved) => {
            setCreating(false);
            if (saved !== undefined) void navigate(`/marketing/leads/${saved.id}?created=1`);
          }}
        />
      )}
    </Stack>
  );
}

/** Leads as a table, linked to their pages. Shared with the campaign page. */
export function LeadTable({ leads }: { leads: readonly LeadDto[] }) {
  const lists = useLeadLists();
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="Leads">
        <TableHead>
          <TableRow>
            <TableCell>Lead ID</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Priority</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Assigned to</TableCell>
            <TableCell>Next action due</TableCell>
            <TableCell>Campaign</TableCell>
            <TableCell>Client</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {leads.map((lead) => (
            <TableRow key={lead.id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <RecordLink to={`/marketing/leads/${lead.id}`}>{lead.code}</RecordLink>
              </TableCell>
              <TableCell>{lead.companyName}</TableCell>
              <TableCell>{valueLabel(lists.data?.status, lead.statusCode)}</TableCell>
              <TableCell>{valueLabel(lists.data?.priority, lead.priorityCode)}</TableCell>
              <TableCell>{valueLabel(lists.data?.source, lead.sourceCode)}</TableCell>
              <TableCell>
                <EmployeeName id={lead.assignedEmployeeId} />
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{lead.nextActionDueDate ?? '—'}</TableCell>
              <TableCell>
                <CampaignRef
                  id={lead.campaignId}
                  code={lead.campaignCode}
                  name={lead.campaignName}
                />
              </TableCell>
              <TableCell>{relationOf(lead)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

interface FormValues {
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  sourceCode: string;
  industryCode: string;
  interest: string;
  priorityCode: string;
  statusCode: string;
  qualificationStatusCode: string;
  nextAction: string;
  nextActionDueDate: string;
}

const OPTIONAL: ReadonlySet<keyof FormValues> = new Set([
  'contactName',
  'contactEmail',
  'contactPhone',
  'sourceCode',
  'industryCode',
  'interest',
  'priorityCode',
  'qualificationStatusCode',
  'nextAction',
  'nextActionDueDate',
]);

function valuesOf(lead: LeadDto | undefined): FormValues {
  return {
    companyName: lead?.companyName ?? '',
    contactName: lead?.contactName ?? '',
    contactEmail: lead?.contactEmail ?? '',
    contactPhone: lead?.contactPhone ?? '',
    sourceCode: lead?.sourceCode ?? '',
    industryCode: lead?.industryCode ?? '',
    interest: lead?.interest ?? '',
    priorityCode: lead?.priorityCode ?? '',
    statusCode: lead?.statusCode ?? '',
    qualificationStatusCode: lead?.qualificationStatusCode ?? '',
    nextAction: lead?.nextAction ?? '',
    nextActionDueDate: lead?.nextActionDueDate ?? '',
  };
}

/**
 * Create (no `lead`) or edit one lead. An edit sends only what changed, with If-Match; an emptied
 * optional field is sent as null ("clear it"). Reassignment and recording a contact have their own
 * actions on the lead page; a new lead names its assignee here.
 */
export function LeadDialog({
  lead,
  onClose,
}: {
  lead?: LeadDto;
  onClose: (saved?: LeadDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const lists = useLeadLists();
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const canSeeCampaigns = hasCapability(session.data, 'marketing.campaign:view');
  const creating = lead === undefined;
  const original = valuesOf(lead);
  const [values, setValues] = useState<FormValues>(original);
  const [assignee, setAssignee] = useState<EmployeeDto | null>(null);
  const [campaign, setCampaign] = useState<CampaignDto | null>(null);
  const [clearCampaign, setClearCampaign] = useState(false);
  const set = (field: keyof FormValues) => (value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const body: Record<string, string | null> = {};
  for (const field of Object.keys(values) as (keyof FormValues)[]) {
    const value = values[field].trim();
    if (creating) {
      if (value !== '') body[field] = value;
    } else if (value !== original[field]) {
      body[field] = OPTIONAL.has(field) && value === '' ? null : value;
    }
  }
  if (creating && assignee !== null) body.assignedEmployeeId = assignee.id;
  if (campaign !== null && campaign.id !== lead?.campaignId) body.campaignId = campaign.id;
  if (!creating && clearCampaign && lead.campaignId !== null) body.campaignId = null;
  const complete =
    values.companyName.trim() !== '' &&
    values.statusCode !== '' &&
    (!creating || assignee !== null);

  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(LEADS, body, LeadSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${LEADS}/${lead.id}`,
            body,
            LeadSchema,
            session.data?.csrfToken ?? '',
            lead.version,
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['marketing', 'leads'] });
      await queryClient.invalidateQueries({ queryKey: ['marketing', 'lead'] });
      onClose(saved);
    },
  });

  const text = (field: keyof FormValues, label: string, type = 'text') => (
    <TextField
      label={label}
      type={type}
      value={values[field]}
      slotProps={type === 'date' ? { inputLabel: { shrink: true } } : {}}
      onChange={(e) => {
        set(field)(e.target.value);
      }}
    />
  );

  return (
    <FormDialog
      open
      title={creating ? 'New lead' : `Edit lead ${lead.code}`}
      submitLabel={creating ? 'Create lead' : 'Save changes'}
      canSubmit={complete && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {creating ? (
        <Alert severity="info" variant="outlined">
          The Lead ID is generated by the system when the lead is saved.
        </Alert>
      ) : (
        <TextField label="Lead ID" value={lead.code} disabled />
      )}
      <TextField
        label="Company"
        required
        autoFocus={creating}
        value={values.companyName}
        onChange={(e) => {
          set('companyName')(e.target.value);
        }}
      />
      {text('contactName', 'Contact name')}
      {text('contactEmail', 'Contact email', 'email')}
      {text('contactPhone', 'Contact phone')}
      <ListSelect
        label="Status"
        required
        values={lists.data?.status}
        value={values.statusCode}
        current={lead?.statusCode}
        onChange={set('statusCode')}
      />
      <ListSelect
        label="Priority"
        values={lists.data?.priority}
        value={values.priorityCode}
        current={lead?.priorityCode}
        onChange={set('priorityCode')}
      />
      <ListSelect
        label="Source"
        values={lists.data?.source}
        value={values.sourceCode}
        current={lead?.sourceCode}
        onChange={set('sourceCode')}
      />
      <ListSelect
        label="Industry"
        values={lists.data?.industry}
        value={values.industryCode}
        current={lead?.industryCode}
        onChange={set('industryCode')}
      />
      <ListSelect
        label="Qualification status"
        values={lists.data?.qualificationStatus}
        value={values.qualificationStatusCode}
        current={lead?.qualificationStatusCode}
        onChange={set('qualificationStatusCode')}
      />
      <TextField
        label="Interest"
        multiline
        minRows={2}
        value={values.interest}
        onChange={(e) => {
          set('interest')(e.target.value);
        }}
      />
      {creating &&
        (canSeeEmployees ? (
          <ActiveEmployeePicker
            label="Assigned to"
            required
            value={assignee}
            onChange={setAssignee}
            helperText="The employee responsible for following the lead up."
          />
        ) : (
          <Alert severity="warning" variant="outlined">
            Choosing the assigned employee needs permission to view employees.
          </Alert>
        ))}
      {canSeeCampaigns && (
        <>
          <CampaignPicker
            label={creating || lead.campaignId === null ? 'Campaign' : 'Change campaign'}
            value={campaign}
            onChange={(value) => {
              setCampaign(value);
              setClearCampaign(false);
            }}
            helperText="The campaign that generated the lead, if any."
          />
          {!creating && lead.campaignId !== null && campaign === null && (
            <TextField
              select
              label="Current campaign"
              value={clearCampaign ? 'clear' : 'keep'}
              onChange={(e) => {
                setClearCampaign(e.target.value === 'clear');
              }}
            >
              <MenuItem value="keep">
                Keep {lead.campaignCode} — {lead.campaignName}
              </MenuItem>
              <MenuItem value="clear">No campaign</MenuItem>
            </TextField>
          )}
        </>
      )}
      {creating && (
        <>
          {text('nextAction', 'Next action')}
          {text('nextActionDueDate', 'Next action due', 'date')}
        </>
      )}
    </FormDialog>
  );
}
